import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronsDown, ChevronsUp, ChevronUp, ChevronDown, Share2 } from 'lucide-react';

// slice 02.1 · paso b.5e — presentacional. El shell portalizado del panel de detalle de una
// tarea: overlay + contenedor + header (back/collapse/título/compartir) + composición de
// `TaskTagsRow` + el contenido de info + `TaskCommentsPanel`. Era el
// `{activeTask && createPortal(…, portalTarget)}` de `ComponentTaskHoy`.
//
// A diferencia de `TaskTagsRow`/`TaskCommentsPanel` (que reciben data + `on*` planos), acá los
// hijos ya vienen armados como slots (`tagsRow`/`infoContent`/`commentsPanel`/
// `commentEditorModals`) -- son elementos ya compuestos por el contenedor con TODOS sus propios
// props (~40-50 cada uno). Re-enhebrar cada uno de esos props a través de este shell sólo para
// que termine en el mismo lugar habría inflado este componente a 90+ props sin ganar nada; pasar
// el elemento ya armado es la misma composición que pide el sub-plan, sin la explosión de props.
//
// El `<input>` de título usa `key={activeTask.id}` (no controlado, `defaultValue`) en vez del
// viejo `tempTitle` + efecto de reset por cambio de tarea -- el remount por key ya resetea el
// valor visible; el contenedor sigue dueño del debounce de 800ms + su guardado.
export const TaskDetailPanel = ({
    activeTask,
    portalTarget,
    standaloneMode,
    standaloneTaskId,
    propStandaloneTaskId,
    onPrevTask,
    onNextTask,
    isTagsCollapsed,
    onToggleCollapse,
    onClose,
    onBackToHome,
    onTitleChange,
    onTitleBlur,
    onShare,
    tagsRow,
    infoContent,
    commentsPanel,
    commentEditorModals,
    // Integración main→dev, epic D1.4 — Cuadro de Trabajo. `headerExtra` = un slot en el header
    // (el `<TaskWorkframeDropdown>`); `renderWorkframe(body)` envuelve el `[left, comments]` con
    // las 3 columnas del panel del cuadro de trabajo (identidad si no viene); `withWorkframe`
    // agrega la clase que reajusta el layout del overlay/container.
    headerExtra,
    renderWorkframe,
    withWorkframe = false,
}) => {
    const { t: translate } = useTranslation('tasks');

    const panelBody = (
        <>
            <div className="task-panel-left">
                <div className="task-panel-left-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {standaloneMode && (
                        <button
                            type="button"
                            className="task-panel-back-btn"
                            onClick={onBackToHome}
                            title={translate('modal.backToHome')}
                            aria-label={translate('modal.backToHome')}
                        >
                            <ChevronLeft size={18} />
                        </button>
                    )}
                    {standaloneMode && (
                        <button
                            type="button"
                            onClick={onToggleCollapse}
                            style={{
                                background: isTagsCollapsed ? '#a855f7' : 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: isTagsCollapsed ? '#fff' : '#888',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 'var(--space-1)',
                                borderRadius: '50%',
                                transition: 'all 0.2s',
                                flexShrink: 0
                            }}
                            title={isTagsCollapsed ? 'Descontraer opciones' : 'Contraer opciones'}
                            aria-label={isTagsCollapsed ? 'Descontraer opciones' : 'Contraer opciones'}
                        >
                            {isTagsCollapsed ? <ChevronsDown size={18} /> : <ChevronsUp size={18} />}
                        </button>
                    )}
                    <input
                        key={activeTask.id}
                        type="text"
                        aria-label={translate('modal.titleAriaLabel')}
                        defaultValue={activeTask.title}
                        onChange={(e) => onTitleChange(e.target.value)}
                        onBlur={(e) => onTitleBlur(e.target.value)}
                        className="task-panel-title-input"
                        placeholder={translate('modal.titlePlaceholder')}
                        style={{ flex: 1, minWidth: 0 }}
                    />
                </div>

                {!isTagsCollapsed && tagsRow}
                {!isTagsCollapsed && infoContent}
            </div>

            {/* Right part: task-panel-comments */}
            {commentsPanel}
        </>
    );

    const overlayVariant = standaloneMode ? 'fullscreen' : (propStandaloneTaskId ? 'standalone' : '');

    return createPortal(
        <div
            className={`task-panel-overlay ${overlayVariant} ${withWorkframe ? 'with-workframe' : ''}`}
            onClick={!standaloneTaskId ? onClose : undefined}
        >
            <div
                className={`task-panel-container ${isTagsCollapsed ? 'collapsed-layout' : ''} ${withWorkframe ? 'with-workframe' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {!standaloneMode && (
                    <button className="task-panel-close-btn" onClick={onClose} title={translate('modal.close')} aria-label={translate('modal.close')}>
                        <X size={11} />
                    </button>
                )}
                {propStandaloneTaskId && onPrevTask && (
                    <button className="task-panel-nav-btn prev-btn" onClick={onPrevTask} title="Notificación anterior" aria-label="Notificación anterior">
                        <ChevronUp size={16} />
                    </button>
                )}
                {propStandaloneTaskId && onNextTask && (
                    <button className="task-panel-nav-btn next-btn" onClick={onNextTask} title="Siguiente notificación" aria-label="Siguiente notificación">
                        <ChevronDown size={16} />
                    </button>
                )}

                {renderWorkframe ? renderWorkframe(panelBody) : panelBody}

                {/* Espacio de trabajo expandido -- Decisión #3 del plan (editor-comentarios-tareas.md):
                    el usuario elige inline o modal para crear o editar, sin perder lo ya escrito. */}
                {commentEditorModals}
            </div>
        </div>,
        portalTarget
    );
};

export default TaskDetailPanel;
